import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  SINAV_DERSLERI, MUFREDATLAR, mufredatBilgi, kazanimSozleri,
  konuSinavda, konuAlaninda, hexToRgba,
} from '../lib/dersler';
import { useAuth } from '../context/AuthContext';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { SinavBolumleri, BolumLejanti, dersKutuSayilari } from './ui/SinavBolumleri';
import { cn } from './ui/cn';

const TOKEN_ANAHTAR = 'yks_token';

// Öğrenme alanı / ünite → renk. Matematik 3 sabit alan; bilinmeyen (kimya ünitesi,
// Maarif teması vb.) için sabit hash ile paletten renk. Not: math JSON'da alan adları
// BÜYÜK harf ("SAYILAR VE CEBİR") — eski bileşen title-case anahtar kullandığı için
// tüm rozetler maviye düşüyordu; burada doğru anahtarlarla düzeltildi.
const ALAN_RENK = {
  'SAYILAR VE CEBİR': '#2563eb',
  'GEOMETRİ': '#16a34a',
  'VERİ, SAYMA VE OLASILIK': '#9333ea',
};
const PALET = ['#2563eb', '#16a34a', '#9333ea', '#e11d48', '#ea580c', '#0891b2', '#ca8a04', '#7c3aed', '#0d9488', '#db2777'];

function alanRenk(alan) {
  if (ALAN_RENK[alan]) return ALAN_RENK[alan];
  let h = 0;
  const s = alan || '';
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PALET[h % PALET.length];
}

// Sınav sekmesi renkleri (ders bağımsız — sınav kimliği).
const SINAV_RENK = {
  TYT: 'bg-slate-700 text-white',
  AYT: 'bg-indigo-600 text-white',
};

const SIRALAMALAR = [
  { v: 'mufredat', ad: 'Müfredat sırası' },
  { v: 'cok', ad: 'Çok soru önce' },
  { v: 'az', ad: 'Az soru önce' },
];

// Sekme (sınıf/sınav) anahtarı — bir konu için filtreleme anahtarı. Çoğu ders
// gerçek `sinif` (9-12) taşır; bazı dersler (ör. Biyoloji MEB temel ağacı,
// hangi kazanımın hangi sınıfa ait olduğu doğrulanamadığı için `sinif=null`
// tutar) `sinav_tipi` (TYT/AYT) üzerinden gruplanır.
function konuSekmeAnahtari(k) {
  return k.sinif != null ? String(k.sinif) : (k.sinav_tipi || '—');
}

// Bir konu listesinden sekme {anahtar, etiket} çiftlerini türetir. Tüm
// konularda `sinif` null ise TYT/AYT sekmelerine düşer; aksi halde 9-12
// sınıf sekmeleri. Sınav ön-filtresi (2026-07-17) sonrası tek sekme kalırsa
// sekme barı hiç gösterilmez (render tarafında length > 1 koşulu).
function sekmeleriHesapla(konular) {
  if (!konular || konular.length === 0) return [];
  const tumuNull = konular.every(k => k.sinif == null);
  if (tumuNull) {
    const sira = { TYT: 0, AYT: 1 };
    const tipler = [...new Set(konular.map(k => k.sinav_tipi || '—'))]
      .sort((a, b) => (sira[a] ?? 9) - (sira[b] ?? 9));
    return tipler.map(t => ({ anahtar: t, etiket: t }));
  }
  const siniflar = [...new Set(konular.map(k => String(k.sinif)))].sort((a, b) => Number(a) - Number(b));
  return siniflar.map(s => ({ anahtar: s, etiket: `${s}. Sınıf` }));
}

// Bir kazanımın durumu. ÜÇ hâl var, ikisi değil:
//   dolu  — tam bu kazanım koduyla etiketlenmiş soru var
//   ustte — kazanım kodunda soru yok ama üst başlıkta (alt başlık/konu) var,
//           yani soru KODLANMIŞ ama daha sığ bir kodla
//   bos   — zincirin hiçbir yerinde soru yok
// Bu ayrım şart: ölçüm (2026-09-03) MEB matematik tablosunda 1.242 sorunun
// (%25) kazanım koduna değil alt başlık/konu koduna yazıldığını gösterdi
// (9.3.1 → 37 soru, tek kazanımı 9.3.1.1 → 0). İki hâlli bir sayaç bunları
// "boş" ilan edip öğretmeni var olmayan bir üretim boşluğuna yönlendirirdi.
// Diğer 12 tablo %100 leaf kodlu — orada "ustte" hiç oluşmaz.
function kazanimHali(kod, ustKendiPayi, sayilar) {
  if ((sayilar[kod] || 0) > 0) return 'dolu';
  return ustKendiPayi > 0 ? 'ustte' : 'bos';
}

// Bir düğümün KENDİ koduna yazılmış soru sayısı. /soru-sayilari prefix rollup
// döndürür (9.1.1 = kendi kodu + bütün çocukları), bu yüzden "üst kodda soru
// var mı" sorusu ham rollup'la sorulamaz: kardeş kazanımın soruları da üst
// düğüme sayılır, her boş kazanım "üst kodda" görünürdü. Fark alınır.
function kendiPayi(kod, cocukKodlari, sayilar) {
  const toplam = sayilar[kod] || 0;
  const cocuk = cocukKodlari.reduce((s, c) => s + (sayilar[c] || 0), 0);
  return Math.max(0, toplam - cocuk);
}

// Alt başlıkları farklı sınıflarda olan konu (alt başlıkta `sinif` alanı; ör. Türkçe
// TDE.9 "Fiil ve Fiilimsi": fiiller 10, kip/fiilimsi/çatı 11 — DÖP 2026) her sınıfa
// kendi alt başlıklarıyla bölünür; kod aynı kalır. Bölünmüş kopyanın soru sayısı
// kendi alt başlıklarının toplamıdır (konu kodu rollup'ı iki kopyayı birden sayar).
function sinifaGoreBol(konular) {
  return konular.flatMap((k) => {
    const altlar = k.alt_konular || [];
    const sinifi = (a) => (a.sinif != null ? a.sinif : k.sinif);
    const siniflar = [...new Set(altlar.map(sinifi))];
    if (siniflar.length < 2) return [k];
    const tumAltKodlar = altlar.map(a => a.kod);
    return siniflar.map(s => ({ ...k, sinif: s, alt_konular: altlar.filter(a => sinifi(a) === s), _tumAltKodlar: tumAltKodlar }));
  });
}
function konuSoru(k, sayilar) {
  if (!k._tumAltKodlar) return sayilar[k.kod] || 0;
  return k.alt_konular.reduce((s, a) => s + (sayilar[a.kod] || 0), 0);
}

// Konu başına: soru sayısı + kazanım kapsaması. Alt başlığı/kazanımı olmayan
// düğümler savunmacı biçimde kendileri leaf sayılır (bugün tüm haritalar
// 3 seviyeli, ama harita üreticileri değişebilir).
function konuIstatistik(k, sayilar) {
  const soru = konuSoru(k, sayilar);
  const altlar = k.alt_konular || [];
  if (!altlar.length) {
    const hal = kazanimHali(k.kod, 0, sayilar);
    return { soru, leaf: 1, dolu: hal === 'dolu' ? 1 : 0, ustte: 0, bos: hal === 'bos' ? 1 : 0 };
  }
  const konuKendi = kendiPayi(k.kod, k._tumAltKodlar || altlar.map(a => a.kod), sayilar);
  let leaf = 0, dolu = 0, ustte = 0, bos = 0;
  for (const a of altlar) {
    const kazanimlar = a.kazanimlar || [];
    if (!kazanimlar.length) {
      leaf += 1;
      const hal = kazanimHali(a.kod, konuKendi, sayilar);
      if (hal === 'dolu') dolu += 1; else if (hal === 'ustte') ustte += 1; else bos += 1;
      continue;
    }
    const ust = Math.max(kendiPayi(a.kod, kazanimlar.map(z => z.kod), sayilar), konuKendi);
    for (const z of kazanimlar) {
      leaf += 1;
      const hal = kazanimHali(z.kod, ust, sayilar);
      if (hal === 'dolu') dolu += 1; else if (hal === 'ustte') ustte += 1; else bos += 1;
    }
  }
  return { soru, leaf, dolu, ustte, bos };
}

// Arama sorgusu konuya (veya altındaki herhangi bir düğüme) uyuyor mu?
function konuEslesir(k, q) {
  if (k.kod.includes(q) || (k.ad || '').toLowerCase().includes(q)) return true;
  return (k.alt_konular || []).some(a =>
    a.kod.includes(q) || (a.ad || '').toLowerCase().includes(q) ||
    (a.kazanimlar || []).some(z => z.kod.includes(q) || (z.ad || '').toLowerCase().includes(q))
  );
}

// 2026-07-17 panel ergonomi: hiyerarşi [TYT | AYT] → ders pilleri → kazanım ağacı.
// 2026-09-03: araya MÜFREDAT boyutu girdi. Ağaç eskiden yalnız MEB_2018 haritasını
// çekiyordu (fetch'e ?mufredat hiç eklenmiyordu) — Maarif havuzu, panelin en büyük
// parçası olmasına rağmen kazanım kırılımında görünmüyordu. Artık her pil için iki
// müfredatın ağacı + sayıları birlikte yüklenir, segment ile geçilir.
// onKazanimFiltrele (opsiyonel): ağaçtaki soru rozetine tıklanınca App'in soru
// listesini o ders(ler)+kazanıma filtreler — {dersDegerleri, konuKod, kod, mufredat, satis?}.
// satilanModu (2026-10-21, kullanıcı isteği): başlıktaki "Satılan" düğmesi — 'gizle' iken
// satılanlar bölümün hiçbir sayısına girmez, 'goster' iken payları ayrıca görünür; null
// (düğmesi olmayan roller) bugünkü davranış.
function KonuDagilimi({ apiUrl, onKazanimFiltrele, satilanModu = null }) {
  const { izinliDersler } = useAuth();
  const [sinav, setSinav] = useState('TYT');
  const [aktifPillKey, setAktifPillKey] = useState('tyt_matematik');
  const [mufredat, setMufredat] = useState(MUFREDATLAR[0].v);
  // ders-ozeti yanıtı: { sayilar, maarif, satilan, satilanMaarif } — ders değeri → sayı.
  const [dersOzet, setDersOzet] = useState({ sayilar: {}, maarif: {}, satilan: {}, satilanMaarif: {} });
  const [paket, setPaket] = useState(null);            // { MEB_2018: {...}, Maarif_2024: {...} }
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [filtreSinif, setFiltreSinif] = useState('9');
  const [arama, setArama] = useState('');
  const [siralama, setSiralama] = useState('mufredat');
  const [yalnizBos, setYalnizBos] = useState(false);
  const [acikKonular, setAcikKonular] = useState(new Set());
  const [acikAltKonular, setAcikAltKonular] = useState(new Set());
  // Kazanım/konu dağılımı bölmesi çok yer kapladığı için varsayılan kapalı
  // (2026-07-08, kullanıcı isteği) — istenince açılır, ders/sınıf değişse de
  // açık kalır (bağımsız state).
  const [genisletildi, setGenisletildi] = useState(false);

  // Ağaç kazanım haritası başına (matematik+geometri aynı ağacı paylaşır),
  // sayılar pil başına (ders değeri süzgeci pile özel) önbelleklenir.
  const agacRef = useRef({});   // `${kazanimKey}|${mufredat}` -> {meta, konular}
  const sayiRef = useRef({});   // `${pillKey}|${mufredat}`    -> {kod: sayi}

  // Kısıtlı kullanıcı: yalnız izinli ders değeri içeren piller görünür.
  const gorunurPiller = useCallback((s) => {
    const liste = SINAV_DERSLERI[s] || [];
    if (!Array.isArray(izinliDersler) || izinliDersler.length === 0) return liste;
    return liste.filter(p => p.dersDegerleri.some(dv => izinliDersler.includes(dv)));
  }, [izinliDersler]);

  const pilller = useMemo(() => gorunurPiller(sinav), [gorunurPiller, sinav]);

  // Aktif pil: seçili key görünür listede yoksa ilk görünür pile düş.
  const aktifPill = useMemo(
    () => pilller.find(p => p.key === aktifPillKey) || pilller[0] || null,
    [pilller, aktifPillKey]
  );

  // Rozet tıklamasında geçilecek ders değerleri (izinli süzgeçli).
  const hedefDersler = useMemo(() => {
    const dd = aktifPill ? aktifPill.dersDegerleri : [];
    return (Array.isArray(izinliDersler) && izinliDersler.length > 0)
      ? dd.filter(dv => izinliDersler.includes(dv)) : dd;
  }, [aktifPill, izinliDersler]);

  // Ders özeti (pill rozetleri) — bir kez yüklenir.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_ANAHTAR);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${apiUrl}/api/kazanim/ders-ozeti`, { headers })
      .then(r => r.json())
      .then(d => setDersOzet({ sayilar: d.sayilar || {}, maarif: d.maarif || {}, satilan: d.satilan || {}, satilanMaarif: d.satilan_maarif || {} }))
      .catch(() => { /* özet kritik değil; pill 0 gösterir */ });
  }, [apiUrl]);

  const satisHaric = satilanModu === 'gizle';

  // Aktif pil için HER İKİ müfredatın ağacı + soru sayıları (önbellekli).
  // İki müfredat birlikte yüklenir çünkü segment butonları kendi soru sayılarını
  // gösterir ve verisi olmayan müfredat pasifleşir — bunu bilmek için ağacı
  // görmek gerekir.
  const veriYukle = useCallback(async (pill, dersDegerleri, haric) => {
    if (!pill) { setPaket(null); setYukleniyor(false); return; }
    const token = localStorage.getItem(TOKEN_ANAHTAR);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const ddQs = (dersDegerleri.length
      ? `&ders_degerleri=${encodeURIComponent(dersDegerleri.join(','))}` : '')
      + (haric ? '&satis=haric' : '');
    const mod = haric ? 'haric' : 'tum';

    const anahtarlar = MUFREDATLAR.map(m => [`${pill.kazanimKey}|${m.v}`, `${pill.key}|${m.v}|${mod}`]);
    const tumuOnbellekte = anahtarlar.every(([a, s]) => agacRef.current[a] && sayiRef.current[s]);

    const getir = async (m) => {
      const mufQs = m.v === 'Maarif_2024' ? '&mufredat=Maarif_2024' : '';
      const agacKey = `${pill.kazanimKey}|${m.v}`;
      const sayiKey = `${pill.key}|${m.v}|${mod}`;
      const isler = [];
      if (!agacRef.current[agacKey]) {
        isler.push(
          fetch(`${apiUrl}/api/kazanim?ders=${encodeURIComponent(pill.kazanimKey)}${mufQs}`, { headers })
            .then(r => r.json())
            .then(d => { agacRef.current[agacKey] = (d && !d.hata) ? d : { meta: {}, konular: [] }; })
        );
      }
      if (!sayiRef.current[sayiKey]) {
        isler.push(
          fetch(`${apiUrl}/api/kazanim/soru-sayilari?ders=${encodeURIComponent(pill.kazanimKey)}${mufQs}${ddQs}`, { headers })
            .then(r => r.json())
            .then(d => { sayiRef.current[sayiKey] = d.sayilar || {}; })
        );
      }
      if (isler.length) await Promise.all(isler);

      const agac = agacRef.current[agacKey] || { meta: {}, konular: [] };
      // Backend, o ders için istenen müfredatın haritası yoksa SESSİZCE MEB'e
      // düşer (edebiyatın yalnız Maarif haritası var; "2018" isteği de onu
      // döndürür). meta.mufredat_sistemi tek güvenilir sinyal — eşleşmiyorsa
      // o müfredat bu derste "yok" sayılır, segment pasifleşir.
      const gecerli = String((agac.meta || {}).mufredat_sistemi || '') === m.v;
      return [m.v, {
        konular: gecerli ? (agac.konular || []) : [],
        sayilar: gecerli ? (sayiRef.current[sayiKey] || {}) : {},
        gecerli,
      }];
    };

    // Önbellek tamsa "Yükleniyor…" hiç gösterilmez — pil/sınav gidip gelmesinde
    // ekranın boşalıp dolması gözü yoruyordu.
    if (!tumuOnbellekte) setYukleniyor(true);
    setHata(null);
    try {
      const ciftler = await Promise.all(MUFREDATLAR.map(getir));
      setPaket(Object.fromEntries(ciftler));
    } catch (e) {
      setHata(e.message || String(e));
    } finally {
      setYukleniyor(false);
    }
  }, [apiUrl]);

  useEffect(() => { veriYukle(aktifPill, hedefDersler, satisHaric); }, [aktifPill, hedefDersler, satisHaric, veriYukle]);

  const filtreleriSifirla = () => {
    setArama('');
    setYalnizBos(false);
    setAcikKonular(new Set());
    setAcikAltKonular(new Set());
  };

  const pillSec = (key) => {
    if (key === aktifPillKey) return;
    filtreleriSifirla();
    setAktifPillKey(key);
  };

  // Sınav değişince aynı etiketli (Matematik→Matematik) karşılık pile geç;
  // yoksa hedef sınavın ilk görünür pili.
  const sinavSec = (yeniSinav) => {
    if (yeniSinav === sinav) return;
    const hedefler = gorunurPiller(yeniSinav);
    const karsilik = (aktifPill && hedefler.find(p => p.label === aktifPill.label)) || hedefler[0];
    setSinav(yeniSinav);
    setAktifPillKey(karsilik ? karsilik.key : '');
    filtreleriSifirla();
  };

  const mufredatSec = (v) => {
    if (v === mufredat) return;
    filtreleriSifirla();
    setMufredat(v);
  };

  // Her müfredat için: sınava + pilin öğrenme alanına süzülmüş konular ve o
  // kümenin soru toplamı (segment rozeti). Ağaç önce sınava (TYT/AYT — sinav_tipi
  // ya da MEB matematik'te sınıf), sonra pilin öğrenme-alanı kuralına süzülür;
  // Maarif'te geometri ayrımı konu koduyla yapılır (bkz konuAlaninda).
  const mufredatOzetleri = useMemo(() => {
    const cikti = {};
    for (const m of MUFREDATLAR) {
      const p = (paket && paket[m.v]) || { konular: [], sayilar: {}, gecerli: false };
      const konular = sinifaGoreBol(p.konular || []).filter(
        k => konuSinavda(k, sinav) && konuAlaninda(k, aktifPill?.ogrenmeAlani, m.v)
      );
      const soru = konular.reduce((s, k) => s + konuSoru(k, p.sayilar), 0);
      cikti[m.v] = { ...p, sinavKonulari: konular, soru, dolu: p.gecerli && konular.length > 0 };
    }
    return cikti;
  }, [paket, sinav, aktifPill]);

  // Aktif müfredatta bu derste ağaç yoksa (ör. AYT Kimya'da Maarif haritası yok)
  // veri olan müfredata düş — boş ekran yerine dolu olanı göster.
  useEffect(() => {
    if (!paket) return;
    setMufredat(prev => {
      if (mufredatOzetleri[prev] && mufredatOzetleri[prev].dolu) return prev;
      const ilk = MUFREDATLAR.find(m => mufredatOzetleri[m.v] && mufredatOzetleri[m.v].dolu);
      return ilk ? ilk.v : prev;
    });
  }, [paket, mufredatOzetleri]);

  const aktifVeri = mufredatOzetleri[mufredat] || { sinavKonulari: [], sayilar: {}, gecerli: false, dolu: false };
  const soruSayilari = aktifVeri.sayilar;
  const sinavKonulari = aktifVeri.sinavKonulari;

  const sinifSekmeleri = useMemo(() => sekmeleriHesapla(sinavKonulari), [sinavKonulari]);

  // filtreSinif'i geçerli sekmelere hizala (pil/sınav/müfredat değişiminde).
  // `some` guard'ı sayesinde geçerliyken dokunulmaz — döngü riski yok.
  useEffect(() => {
    if (!sinifSekmeleri.length) return;
    setFiltreSinif(prev => (sinifSekmeleri.some(s => s.anahtar === prev) ? prev : sinifSekmeleri[0].anahtar));
  }, [sinifSekmeleri]);

  const aramaQ = arama.trim().toLowerCase();

  const konular = useMemo(() => {
    let list = sinavKonulari.filter(k => konuSekmeAnahtari(k) === filtreSinif);
    if (aramaQ) list = list.filter(k => konuEslesir(k, aramaQ));
    if (yalnizBos) list = list.filter(k => konuIstatistik(k, soruSayilari).bos > 0);
    if (siralama !== 'mufredat') {
      const yon = siralama === 'cok' ? -1 : 1;
      list = [...list].sort((a, b) => yon * (konuSoru(a, soruSayilari) - konuSoru(b, soruSayilari)));
    }
    return list;
  }, [sinavKonulari, filtreSinif, aramaQ, yalnizBos, siralama, soruSayilari]);

  const ozet = useMemo(() => {
    let altBaslik = 0, kazanim = 0, soru = 0, dolu = 0, ustte = 0, bos = 0, enCok = 0;
    for (const k of konular) {
      altBaslik += (k.alt_konular || []).length;
      const ist = konuIstatistik(k, soruSayilari);
      kazanim += ist.leaf;
      dolu += ist.dolu;
      ustte += ist.ustte;
      bos += ist.bos;
      soru += ist.soru;
      if (ist.soru > enCok) enCok = ist.soru;
    }
    return { konu: konular.length, altBaslik, kazanim, dolu, ustte, bos, soru, enCok };
  }, [konular, soruSayilari]);

  // Boş kazanım sayısı sınıf sekmesinden bağımsız olmalı — "bu derste kaç
  // boşluk var" sorusu sekme değişince kaybolmamalı; şerit çipinde gösterilir.
  const sinifBagimsizBos = useMemo(
    () => sinavKonulari.reduce((s, k) => s + konuIstatistik(k, soruSayilari).bos, 0),
    [sinavKonulari, soruSayilari]
  );

  const konuToggle = (kod) =>
    setAcikKonular(prev => { const n = new Set(prev); n.has(kod) ? n.delete(kod) : n.add(kod); return n; });
  const altKonuToggle = (kod) =>
    setAcikAltKonular(prev => { const n = new Set(prev); n.has(kod) ? n.delete(kod) : n.add(kod); return n; });

  // Rozet tıkla-filtrele: konu/alt-konu/kazanım soru sayacından App listesine in.
  // Müfredat da taşınır — Maarif kazanımına tıklayınca filtre barındaki müfredat
  // toggle'ı da Maarif'e geçsin (aksi halde liste doğru gelir ama toggle "Hepsi"
  // görünür, kullanıcı hangi havuza baktığını okuyamaz).
  const rozetTikla = (e, konuKod, kod) => {
    e.stopPropagation();
    if (onKazanimFiltrele && hedefDersler.length) {
      // Rozetteki sayı ile listenin sayısı tutsun: satılan gizliyken liste de satılmamışlara iner.
      const satis = satilanModu ? { satis: satisHaric ? 'satilmamis' : '' } : {};
      onKazanimFiltrele({ dersDegerleri: hedefDersler, konuKod, kod, mufredat, ...satis });
    }
  };
  const tiklanabilir = !!onKazanimFiltrele && hedefDersler.length > 0;

  const aktifRenk = aktifPill ? aktifPill.renk : '#2563EB';
  const mBilgi = mufredatBilgi(mufredat);
  // Maarif ağacında "öğrenme çıktısı", MEB'de "kazanım" (2026-10-09, kullanıcı kararı).
  const soz = kazanimSozleri(mufredat);
  // Arama ya da boşluk avı sırasında ağaç kendiliğinden açılır — eşleşme
  // kapalı bir düğümün içinde kalırsa filtre sessizce sonuçsuz görünüyordu.
  const otoAcik = !!aramaQ || yalnizBos;

  return (
    <Card className="mb-4 px-3 py-2">
      {/* SINAV + DERS SEÇİMİ: [TYT | AYT] (sınavın toplam sorusuyla) → ÖSYM kitapçık
          bölümleri altında dersler (2026-10-21). 2026-10-31: daha sıkı; Card'ın varsayılan
          dili (rounded-xl, gray-200 kenar, shadow-sm) — üst başlık ve filtre çubuğuyla aynı. */}
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div className="inline-flex rounded-lg bg-slate-100 p-0.5" role="tablist" aria-label="Sınav">
          {['TYT', 'AYT'].map(s => {
            const piller = gorunurPiller(s);
            if (!piller.length) return null;
            const toplam = piller.reduce((t, p) => t + (p.aktif ? dersKutuSayilari(p, dersOzet, satilanModu).sayi : 0), 0);
            return (
              <button
                key={s}
                role="tab"
                aria-selected={sinav === s}
                onClick={() => sinavSec(s)}
                className={cn(
                  'flex items-baseline gap-2 rounded-md px-3 py-1 transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400',
                  sinav === s ? SINAV_RENK[s] + ' shadow-sm' : 'text-slate-500 hover:text-slate-800'
                )}
              >
                <span className="text-sm font-bold">{s}</span>
                <span className={cn('text-xs font-semibold tabular-nums', sinav === s ? 'text-white/75' : 'text-slate-400')}>
                  {toplam.toLocaleString('tr-TR')}
                </span>
              </button>
            );
          })}
        </div>
        {/* Açılır-kapanır başlık — bölme varsayılan kapalı, istenince açılır. 2026-10-31:
            ayrı satır yerine TYT/AYT ile lejant arasında (dikeyde bir satır kazanılır). */}
        <button
          type="button"
          onClick={() => setGenisletildi(v => !v)}
          aria-expanded={genisletildi}
          className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-lg px-2 py-1 transition-colors hover:bg-slate-50"
        >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <svg viewBox="0 0 16 16" aria-hidden="true" className={cn('h-3.5 w-3.5 text-slate-400 transition-transform motion-reduce:transition-none', genisletildi && 'rotate-90')}>
            <path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {soz.Adlar} ve soru dağılımı
          {aktifPill && (
            <span className="text-xs font-medium text-gray-400">— {sinav} {aktifPill.label}</span>
          )}
        </span>
        {/* Kapalıyken en değerli bilgi iki müfredatın havuzu — açmadan görünür. */}
        {!yukleniyor && !hata && paket && (
          <span className="flex items-center gap-1.5 text-xs flex-wrap">
            {MUFREDATLAR.map(m => (
              <Badge
                key={m.v}
                renk={mufredatOzetleri[m.v] && mufredatOzetleri[m.v].dolu ? m.renk : undefined}
                variant="muted"
                className="font-semibold"
                title={`${m.ad} — ${m.yks}`}
              >
                {m.kisa} {mufredatOzetleri[m.v] && mufredatOzetleri[m.v].dolu
                  ? mufredatOzetleri[m.v].soru.toLocaleString('tr-TR')
                  : 'yok'}
              </Badge>
            ))}
          </span>
        )}
        </button>
        <BolumLejanti mod={satilanModu} />
      </div>

      <SinavBolumleri
        sinav={sinav}
        aileler={pilller}
        aktifKey={aktifPill ? aktifPill.key : ''}
        onSec={pillSec}
        ozet={dersOzet}
        satilanModu={satilanModu}
      />

      {genisletildi && (yukleniyor ? (
        <div className="py-8 text-center text-gray-400 text-sm">Yükleniyor…</div>
      ) : hata ? (
        <div className="py-6 text-center text-red-600 text-sm">Hata: {hata}</div>
      ) : !paket ? null : (
        <>
          {/* MÜFREDAT — kazanım ağacının hangi müfredattan okunduğu. Her segment
              kendi soru sayısını taşır; ağacı olmayan müfredat pasif. */}
          <div className="flex items-center gap-3 flex-wrap mt-3 mb-3">
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" role="tablist" aria-label="Müfredat">
              {MUFREDATLAR.map(m => {
                const o = mufredatOzetleri[m.v] || {};
                const secili = mufredat === m.v;
                return (
                  <button
                    key={m.v}
                    role="tab"
                    aria-selected={secili}
                    disabled={!o.dolu}
                    onClick={() => mufredatSec(m.v)}
                    title={o.dolu ? `${m.ad} — ${m.yks}` : `${m.ad} ${kazanimSozleri(m.v).ad} ağacı bu derste tanımlı değil`}
                    className={cn(
                      'flex items-baseline gap-1.5 px-3 py-1.5 text-xs font-semibold border-r border-gray-200 last:border-r-0 transition-colors',
                      !o.dolu
                        ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                        : secili ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    )}
                    style={secili && o.dolu ? { backgroundColor: m.renk } : undefined}
                  >
                    {m.ad}
                    <span className={cn('tabular-nums font-bold', secili ? 'text-white/80' : 'text-gray-400')}>
                      {o.dolu ? o.soru.toLocaleString('tr-TR') : '—'}
                    </span>
                  </button>
                );
              })}
            </div>
            <span className="text-[11px] text-gray-400">{mBilgi.yks}</span>
          </div>

          {!aktifVeri.dolu ? (
            <div className="py-6 text-center text-gray-400 text-sm">
              {aktifPill ? `${sinav} ${aktifPill.label}` : 'Bu ders'} için {mBilgi.ad} {soz.ad} ağacı tanımlı değil.
            </div>
          ) : (
            <>
              {/* Sınıf sekmeleri (tek sekme kaldıysa bar gizli) + arama + sıralama */}
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  {sinifSekmeleri.length > 1 && (
                    <div className="flex bg-gray-100 rounded-full p-0.5">
                      {sinifSekmeleri.map(sekme => (
                        <button
                          key={sekme.anahtar}
                          onClick={() => { setFiltreSinif(sekme.anahtar); setArama(''); }}
                          className={cn(
                            'px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors',
                            filtreSinif === sekme.anahtar ? 'text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'
                          )}
                          style={filtreSinif === sekme.anahtar ? { backgroundColor: mBilgi.renk } : undefined}
                        >
                          {sekme.etiket}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Boşluk avı: soru yazılmamış kazanımları öne çıkarır — üretim
                      sırasını belirleyen asıl sinyal bu. */}
                  <button
                    type="button"
                    onClick={() => setYalnizBos(v => !v)}
                    aria-pressed={yalnizBos}
                    disabled={sinifBagimsizBos === 0}
                    title={sinifBagimsizBos === 0
                      ? `Bu derste soru yazılmamış ${soz.ad} kalmadı`
                      : `Soru yazılmamış ${soz.adlari} göster`}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors',
                      sinifBagimsizBos === 0
                        ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                        : yalnizBos
                          ? 'bg-amber-500 border-amber-500 text-white'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-amber-300 hover:text-amber-700'
                    )}
                  >
                    {sinifBagimsizBos === 0 ? `Boş ${soz.ad} yok` : `${sinifBagimsizBos} boş ${soz.ad}`}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={siralama}
                    onChange={e => setSiralama(e.target.value)}
                    aria-label="Sıralama"
                    className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-200"
                  >
                    {SIRALAMALAR.map(s => <option key={s.v} value={s.v}>{s.ad}</option>)}
                  </select>
                  <div className="relative">
                    <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="11" cy="11" r="8" strokeWidth="2" />
                      <path d="m21 21-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <input
                      type="text"
                      value={arama}
                      onChange={e => setArama(e.target.value)}
                      placeholder={`${soz.Ad}, konu veya kod ara...`}
                      className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs w-56 focus:outline-none focus:ring-1 focus:ring-blue-200"
                    />
                  </div>
                </div>
              </div>

              {/* ÖZET — üç hücre: havuz, kazanım gövdesi, kapsama */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-gray-100 rounded-lg overflow-hidden mb-3 border border-gray-100">
                <div className="bg-white px-3 py-2">
                  <div className="text-lg font-bold tabular-nums leading-tight" style={{ color: mBilgi.renk }}>
                    {ozet.soru.toLocaleString('tr-TR')}
                  </div>
                  <div className="text-[11px] text-gray-500">soru</div>
                </div>
                <div className="bg-white px-3 py-2">
                  <div className="text-lg font-bold text-gray-800 tabular-nums leading-tight">{ozet.kazanim}</div>
                  <div className="text-[11px] text-gray-500">
                    {soz.ad}, {ozet.konu} konu içinde
                  </div>
                </div>
                <div className="bg-white px-3 py-2 col-span-2 sm:col-span-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-lg font-bold text-gray-800 tabular-nums leading-tight">
                      {ozet.dolu}/{ozet.kazanim}
                    </span>
                    <span className="text-[11px] text-gray-500">
                      {soz.adda} soru var{ozet.ustte > 0 ? `, ${ozet.ustte}'i üst kodda` : ''}
                    </span>
                  </div>
                  {/* Bar üç dilim: kendi kodunda soru olan · üst kodda kalan · hiç yok */}
                  <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden flex">
                    <div style={{ width: ozet.kazanim ? `${(ozet.dolu / ozet.kazanim) * 100}%` : '0%', backgroundColor: aktifRenk }} />
                    <div style={{ width: ozet.kazanim ? `${(ozet.ustte / ozet.kazanim) * 100}%` : '0%', backgroundColor: hexToRgba(aktifRenk, 0.3) }} />
                  </div>
                </div>
              </div>

              {/* Konu listesi */}
              {konular.length === 0 ? (
                <div className="py-6 text-center text-gray-400 text-sm">
                  {yalnizBos ? `Bu sınıfta boş ${soz.ad} yok.`
                    : aramaQ ? `Eşleşen ${soz.ad} bulunamadı.`
                      : 'Bu sınıfta konu yok.'}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {konular.map(k => {
                    const acik = acikKonular.has(k.kod) || otoAcik;
                    const ist = konuIstatistik(k, soruSayilari);
                    const alan = k.ogrenme_alani || '';
                    const renk = alanRenk(alan);
                    const alanAyni = (k.ad || '').trim() === alan.trim(); // kimya/Maarif: tema=ad
                    const pay = ozet.enCok ? (ist.soru / ozet.enCok) * 100 : 0;
                    const konuKendiPayi = kendiPayi(k.kod, (k.alt_konular || []).map(x => x.kod), soruSayilari);
                    const ustSoru = (a) => Math.max(
                      kendiPayi(a.kod, (a.kazanimlar || []).map(z => z.kod), soruSayilari),
                      konuKendiPayi
                    );
                    const altListe = yalnizBos
                      ? (k.alt_konular || []).filter(a => (a.kazanimlar || []).some(z => kazanimHali(z.kod, ustSoru(a), soruSayilari) === 'bos'))
                      : (k.alt_konular || []);

                    return (
                      <div key={k.kod} className="border border-gray-100 rounded-lg overflow-hidden">
                        <button
                          onClick={() => konuToggle(k.kod)}
                          className="w-full flex items-stretch text-left hover:bg-gray-50/60 transition-colors"
                        >
                          {/* Öğrenme alanı şeridi — rengin taşıdığı bilgi alan kimliği */}
                          <span className="w-1 shrink-0" style={{ backgroundColor: renk }} aria-hidden="true" />
                          <span className="flex-1 min-w-0 px-3.5 py-2.5">
                            <span className="flex items-center gap-3">
                              <span className="flex-1 min-w-0">
                                <span className="flex items-center gap-1.5 flex-wrap">
                                  <span className="font-mono text-xs text-gray-400">{k.kod}</span>
                                  {!alanAyni && (
                                    <span
                                      className="inline-flex items-center px-1.5 py-px rounded text-[10px] font-medium"
                                      style={{ backgroundColor: hexToRgba(renk, 0.1), color: renk }}
                                    >
                                      {alan}
                                    </span>
                                  )}
                                </span>
                                <span className="block text-sm font-bold text-gray-900 leading-tight mt-0.5">{k.ad}</span>
                              </span>
                              <span className="flex items-center gap-2 text-xs shrink-0">
                                <span
                                  className="hidden sm:inline text-[11px] tabular-nums"
                                  title={`${ist.dolu} ${soz.adda} soru var, ${ist.bos} ${soz.ad} boş`
                                    + (ist.ustte > 0 ? `, ${ist.ustte} ${soz.adin} soruları üst kodda` : '')}
                                >
                                  <span className={ist.bos > 0 ? 'text-amber-600 font-semibold' : 'text-gray-400'}>
                                    {ist.dolu}/{ist.leaf}
                                  </span>
                                  <span className="text-gray-400"> {soz.ad}</span>
                                </span>
                                {ist.soru > 0 ? (
                                  /* Konu toggle button'ı içinde iç içe <button> geçersiz →
                                     span role="button". Tıklama listeyi bu konuya filtreler. */
                                  tiklanabilir ? (
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={(e) => rozetTikla(e, k.kod, k.kod)}
                                      onKeyDown={(e) => { if (e.key === 'Enter') rozetTikla(e, k.kod, k.kod); }}
                                      title="Bu konunun sorularını listede filtrele"
                                      className="cursor-pointer rounded-full hover:ring-2 hover:ring-offset-1"
                                      style={{ '--tw-ring-color': hexToRgba(renk, 0.4) }}
                                    >
                                      <Badge renk={renk} className="font-semibold tabular-nums">{ist.soru.toLocaleString('tr-TR')} soru</Badge>
                                    </span>
                                  ) : (
                                    <Badge renk={renk} className="font-semibold tabular-nums">{ist.soru.toLocaleString('tr-TR')} soru</Badge>
                                  )
                                ) : (
                                  <Badge variant="muted">soru yok</Badge>
                                )}
                                <span className="text-gray-300">{acik ? '▾' : '▸'}</span>
                              </span>
                            </span>
                            {/* Havuz payı — bu konu, görünen en yoğun konunun kaçta kaçı */}
                            <span className="block mt-2 h-[3px] rounded-full bg-gray-100 overflow-hidden" aria-hidden="true">
                              <span className="block h-full rounded-full" style={{ width: `${pay}%`, backgroundColor: hexToRgba(renk, 0.55) }} />
                            </span>
                          </span>
                        </button>

                        {acik && (
                          <div className="px-3.5 pb-2.5 pl-5 space-y-1">
                            {altListe.map(a => {
                              const altAcik = acikAltKonular.has(a.kod) || otoAcik;
                              const kazanimlar = a.kazanimlar || [];
                              const haller = new Map(kazanimlar.map(z => [z.kod, kazanimHali(z.kod, ustSoru(a), soruSayilari)]));
                              const gorunenKazanimlar = yalnizBos
                                ? kazanimlar.filter(z => haller.get(z.kod) === 'bos')
                                : kazanimlar;
                              const altDolu = kazanimlar.filter(z => haller.get(z.kod) === 'dolu').length;
                              const altBos = kazanimlar.filter(z => haller.get(z.kod) === 'bos').length;
                              const altSoruSayi = soruSayilari[a.kod] || 0;

                              return (
                                <div key={a.kod}>
                                  <button
                                    onClick={() => altKonuToggle(a.kod)}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 bg-gray-50/70 border border-gray-100 rounded-lg hover:bg-gray-100/70 transition-colors"
                                  >
                                    <span className="text-gray-400 text-[10px] shrink-0">{altAcik ? '▾' : '▸'}</span>
                                    <span
                                      className="px-2 py-px rounded text-[10px] font-mono font-bold text-white shrink-0"
                                      style={{ backgroundColor: mBilgi.renk }}
                                    >
                                      {a.kod}
                                    </span>
                                    <span className="font-semibold text-gray-800 text-xs text-left flex-1 min-w-0 leading-tight">{a.ad}</span>
                                    <span className="flex items-center gap-2 shrink-0 text-[10px]">
                                      <span
                                        className={cn('tabular-nums', altBos > 0 ? 'text-amber-600 font-semibold' : 'text-gray-400')}
                                        title={`${altDolu} ${soz.adda} soru var, ${altBos} ${soz.ad} boş`}
                                      >
                                        {altDolu}/{kazanimlar.length}
                                      </span>
                                      {altSoruSayi > 0 && (
                                        tiklanabilir ? (
                                          <span
                                            role="button"
                                            tabIndex={0}
                                            onClick={(e) => rozetTikla(e, k.kod, a.kod)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') rozetTikla(e, k.kod, a.kod); }}
                                            title="Bu alt başlığın sorularını listede filtrele"
                                            className="tabular-nums font-semibold cursor-pointer hover:underline"
                                            style={{ color: mBilgi.renk }}
                                          >{altSoruSayi.toLocaleString('tr-TR')}</span>
                                        ) : (
                                          <span className="tabular-nums text-gray-400">{altSoruSayi.toLocaleString('tr-TR')}</span>
                                        )
                                      )}
                                    </span>
                                  </button>

                                  {altAcik && (
                                    <div className="ml-8 mt-0.5 mb-1">
                                      {gorunenKazanimlar.map(z => {
                                        const n = soruSayilari[z.kod] || 0;
                                        const hal = haller.get(z.kod);
                                        return (
                                          <div key={z.kod} className="flex items-baseline gap-2 text-[11px] py-0.5 px-2 rounded hover:bg-gray-50 leading-snug">
                                            <span className="font-mono text-[10px] text-gray-400 shrink-0">{z.kod}</span>
                                            <span className={cn('flex-1 min-w-0', hal === 'dolu' ? 'text-gray-600' : 'text-gray-400')}>{z.ad}</span>
                                            {hal === 'dolu' ? (
                                              tiklanabilir ? (
                                                <button
                                                  type="button"
                                                  onClick={(e) => rozetTikla(e, k.kod, z.kod)}
                                                  title={`Bu ${soz.adin} sorularını listede filtrele`}
                                                  className="text-[10px] font-semibold shrink-0 tabular-nums cursor-pointer hover:underline"
                                                  style={{ color: renk }}
                                                >{n.toLocaleString('tr-TR')}</button>
                                              ) : (
                                                <span className="text-[10px] font-semibold shrink-0 tabular-nums" style={{ color: renk }}>{n.toLocaleString('tr-TR')}</span>
                                              )
                                            ) : hal === 'ustte' ? (
                                              /* Soru var ama üst başlığın koduyla etiketlenmiş —
                                                 üretim boşluğu değil, kodlama derinliği eksiği. */
                                              <span
                                                className="text-[10px] shrink-0 text-gray-400"
                                                title={`Bu ${soz.adin} soruları üst başlığın koduyla etiketlenmiş`}
                                              >üst kodda</span>
                                            ) : (
                                              /* Boş kazanım: eskiden hiçbir işaret yoktu, "soru yok"
                                                 ile "sayı gösterilmiyor" ayırt edilemiyordu. */
                                              <span className="text-[10px] shrink-0 text-amber-600/70 font-medium" title={`Bu ${soz.adda} henüz soru yok`}>boş</span>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      ))}
    </Card>
  );
}

export default KonuDagilimi;
